import React, { useState, useEffect } from 'react';
import { Bell, BellOff, Mail, Smartphone, Send, CheckCircle2, XCircle, Loader2, MessageSquare, Phone } from 'lucide-react';
import { toast } from 'sonner';
import usePushNotifications from '../hooks/usePushNotifications';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function NotificationSettings() {
  const {
    isSupported,
    isSubscribed,
    permission,
    loading: pushLoading,
    error: pushError,
    subscribe,
    unsubscribe,
    sendTestNotification
  } = usePushNotifications();

  const [settings, setSettings] = useState({
    push_enabled: true,
    email_enabled: true,
    email: '',
    sms_enabled: false,
    phone_number: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingSending, setTestingSending] = useState(false);
  const [testingSMS, setTestingSMS] = useState(false);
  const [smsHistory, setSmsHistory] = useState([]);

  // Fetch notification settings
  useEffect(() => {
    fetchSettings();
    fetchSMSHistory();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/notifications/settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSMSHistory = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/notifications/sms/history?limit=5`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSmsHistory(data.sms_history || []);
      }
    } catch (error) {
      console.error('Error fetching SMS history:', error);
    }
  };

  const updateSettings = async (updates) => {
    try {
      setSaving(true);
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/notifications/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updates)
      });
      
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
        toast.success('Settings saved');
      }
    } catch (error) {
      console.error('Error updating settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSMSToggle = () => {
    updateSettings({ sms_enabled: !settings.sms_enabled });
  };

  const handlePhoneChange = (e) => {
    setSettings({ ...settings, phone_number: e.target.value });
  };

  const handlePhoneSave = () => {
    updateSettings({ phone_number: settings.phone_number });
  };

  const handleTestSMS = async () => {
    if (!settings.phone_number) {
      toast.error('Please enter a phone number first');
      return;
    }
    try {
      setTestingSMS(true);
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/notifications/sms/test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ phone_number: settings.phone_number })
      });
      if (response.ok) {
        toast.success('Test SMS sent (simulated)!');
        fetchSMSHistory();
      }
    } catch (error) {
      toast.error('Failed to send test SMS');
    } finally {
      setTestingSMS(false);
    }
  };

  const handleTogglePush = async () => {
    if (!isSubscribed) {
      const success = await subscribe();
      if (success) {
        toast.success('Push notifications enabled!');
        updateSettings({ push_enabled: true });
      } else if (permission === 'denied') {
        toast.error('Notifications blocked. Please enable in browser settings.');
      }
    } else {
      const success = await unsubscribe();
      if (success) {
        toast.success('Push notifications disabled');
        updateSettings({ push_enabled: false });
      }
    }
  };

  const handleTestPush = async () => {
    try {
      setTestingSending(true);
      await sendTestNotification();
      toast.success('Test notification sent!');
    } catch (error) {
      toast.error('Failed to send test notification');
    } finally {
      setTestingSending(false);
    }
  };

  const handleEmailToggle = () => {
    updateSettings({ email_enabled: !settings.email_enabled });
  };

  const handleEmailChange = (e) => {
    setSettings({ ...settings, email: e.target.value });
  };

  const handleEmailSave = () => {
    updateSettings({ email: settings.email });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-6 h-6 animate-spin text-green-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Push Notifications */}
      <div className="bg-gray-900/50 rounded-lg p-6 border border-gray-800">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${isSubscribed ? 'bg-green-500/20' : 'bg-gray-700'}`}>
              <Smartphone className={`w-5 h-5 ${isSubscribed ? 'text-green-500' : 'text-gray-400'}`} />
            </div>
            <div>
              <h3 className="text-white font-medium">Push Notifications</h3>
              <p className="text-sm text-gray-400">
                {isSupported ? 'Receive alerts on your device' : 'Not supported in this browser'}
              </p>
            </div>
          </div>
          
          <button
            onClick={handleTogglePush}
            disabled={!isSupported || pushLoading}
            className={`relative w-14 h-7 rounded-full transition-colors ${
              isSubscribed ? 'bg-green-500' : 'bg-gray-600'
            } ${(!isSupported || pushLoading) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
          >
            <div className={`absolute w-5 h-5 bg-white rounded-full top-1 transition-transform ${
              isSubscribed ? 'translate-x-8' : 'translate-x-1'
            }`} />
          </button>
        </div>

        {/* Status indicators */}
        <div className="flex flex-wrap gap-3 mt-4">
          <div className={`flex items-center gap-2 text-sm px-3 py-1.5 rounded-full ${
            isSupported ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'
          }`}>
            {isSupported ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
            {isSupported ? 'Supported' : 'Not Supported'}
          </div>
          
          <div className={`flex items-center gap-2 text-sm px-3 py-1.5 rounded-full ${
            permission === 'granted' ? 'bg-green-500/10 text-green-400' : 
            permission === 'denied' ? 'bg-red-500/10 text-red-400' : 'bg-yellow-500/10 text-yellow-400'
          }`}>
            {permission === 'granted' ? <CheckCircle2 className="w-4 h-4" /> : 
             permission === 'denied' ? <XCircle className="w-4 h-4" /> : <Bell className="w-4 h-4" />}
            Permission: {permission}
          </div>
          
          <div className={`flex items-center gap-2 text-sm px-3 py-1.5 rounded-full ${
            isSubscribed ? 'bg-green-500/10 text-green-400' : 'bg-gray-500/10 text-gray-400'
          }`}>
            {isSubscribed ? <Bell className="w-4 h-4" /> : <BellOff className="w-4 h-4" />}
            {isSubscribed ? 'Subscribed' : 'Not Subscribed'}
          </div>
        </div>

        {/* Test notification button */}
        {isSubscribed && (
          <button
            onClick={handleTestPush}
            disabled={testingSending}
            className="mt-4 flex items-center gap-2 px-4 py-2 bg-green-500/20 text-green-400 rounded-lg hover:bg-green-500/30 transition-colors disabled:opacity-50"
          >
            {testingSending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
            Send Test Notification
          </button>
        )}

        {pushError && (
          <p className="mt-3 text-sm text-red-400">{pushError}</p>
        )}
      </div>

      {/* Email Notifications */}
      <div className="bg-gray-900/50 rounded-lg p-6 border border-gray-800">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${settings.email_enabled ? 'bg-green-500/20' : 'bg-gray-700'}`}>
              <Mail className={`w-5 h-5 ${settings.email_enabled ? 'text-green-500' : 'text-gray-400'}`} />
            </div>
            <div>
              <h3 className="text-white font-medium">Email Notifications</h3>
              <p className="text-sm text-gray-400">Receive alerts via email</p>
            </div>
          </div>
          
          <button
            onClick={handleEmailToggle}
            disabled={saving}
            className={`relative w-14 h-7 rounded-full transition-colors ${
              settings.email_enabled ? 'bg-green-500' : 'bg-gray-600'
            } cursor-pointer`}
          >
            <div className={`absolute w-5 h-5 bg-white rounded-full top-1 transition-transform ${
              settings.email_enabled ? 'translate-x-8' : 'translate-x-1'
            }`} />
          </button>
        </div>

        {settings.email_enabled && (
          <div className="mt-4">
            <label className="block text-sm text-gray-400 mb-2">Notification Email</label>
            <div className="flex gap-2">
              <input
                type="email"
                value={settings.email}
                onChange={handleEmailChange}
                placeholder="your@email.com"
                className="flex-1 px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-green-500"
              />
              <button
                onClick={handleEmailSave}
                disabled={saving}
                className="px-4 py-2 bg-green-500 text-black font-medium rounded-lg hover:bg-green-400 transition-colors disabled:opacity-50"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
              </button>
            </div>
            <p className="mt-2 text-xs text-gray-500">
              Note: Email notifications require SMTP to be configured on the server.
            </p>
          </div>
        )}
      </div>

      {/* SMS Notifications */}
      <div className="bg-gray-900/50 rounded-lg p-6 border border-gray-800">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${settings.sms_enabled ? 'bg-green-500/20' : 'bg-gray-700'}`}>
              <MessageSquare className={`w-5 h-5 ${settings.sms_enabled ? 'text-green-500' : 'text-gray-400'}`} />
            </div>
            <div>
              <h3 className="text-white font-medium">SMS Notifications</h3>
              <p className="text-sm text-gray-400">Receive alerts via text message (simulated)</p>
            </div>
          </div>
          
          <button
            onClick={handleSMSToggle}
            disabled={saving}
            className={`relative w-14 h-7 rounded-full transition-colors ${
              settings.sms_enabled ? 'bg-green-500' : 'bg-gray-600'
            } cursor-pointer`}
          >
            <div className={`absolute w-5 h-5 bg-white rounded-full top-1 transition-transform ${
              settings.sms_enabled ? 'translate-x-8' : 'translate-x-1'
            }`} />
          </button>
        </div>

        {settings.sms_enabled && (
          <div className="mt-4 space-y-4">
            <div>
              <label className="block text-sm text-gray-400 mb-2">Phone Number</label>
              <div className="flex gap-2">
                <div className="flex items-center px-3 bg-gray-800 border border-gray-700 rounded-l-lg text-gray-400">
                  <Phone className="w-4 h-4" />
                </div>
                <input
                  type="tel"
                  value={settings.phone_number || ''}
                  onChange={handlePhoneChange}
                  placeholder="+1 (555) 123-4567"
                  className="flex-1 px-4 py-2 bg-gray-800 border border-gray-700 rounded-r-lg text-white placeholder-gray-500 focus:outline-none focus:border-green-500"
                />
                <button
                  onClick={handlePhoneSave}
                  disabled={saving}
                  className="px-4 py-2 bg-green-500 text-black font-medium rounded-lg hover:bg-green-400 transition-colors disabled:opacity-50"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
                </button>
              </div>
            </div>
            
            {/* Test SMS button */}
            <button
              onClick={handleTestSMS}
              disabled={testingSMS || !settings.phone_number}
              className="flex items-center gap-2 px-4 py-2 bg-green-500/20 text-green-400 rounded-lg hover:bg-green-500/30 transition-colors disabled:opacity-50"
            >
              {testingSMS ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
              Send Test SMS
            </button>

            {/* SMS History */}
            {smsHistory.length > 0 && (
              <div className="mt-4">
                <h4 className="text-sm text-gray-400 mb-2">Recent SMS Notifications</h4>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {smsHistory.map((sms, i) => (
                    <div key={sms.id || i} className="p-3 bg-gray-800/50 rounded-lg border border-gray-700">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-green-400 font-mono">{sms.type}</span>
                        <span className="text-xs text-gray-500">
                          {new Date(sms.created_at).toLocaleString()}
                        </span>
                      </div>
                      <p className="text-sm text-gray-300">{sms.message}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <p className="text-xs text-gray-500">
              Note: SMS notifications are simulated. In production, this would integrate with Twilio or similar services.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
